import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Menu, Play, Server } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Button from "../../components/Button/Button";
import SidebarMenu from "../../components/ApiCatalogue/SidebarMenu";
import ApiIntro from "../../components/ApiCatalogue/ApiIntro";
import ApiDetailsCard from "../../components/ApiCatalogue/ApiDetailsCard";
import RequestHeaders from "../../components/ApiCatalogue/RequestHeaders";
import RequestBodyParams from "../../components/ApiCatalogue/RequestBodyParams";
import RequestBodyFormate from "../../components/ApiCatalogue/RequestBodyFormate";
import ResponseBodyParams from "../../components/ApiCatalogue/ResponseBodyParams";
import ErrorHandling from "../../components/ApiCatalogue/ErrorHandling";
import SupportBox from "../../components/ApiCatalogue/SupportBox";
import CodeSample from "../../components/ApiCatalogue/CodeSample";
import LosApiDocs from "../../components/ApiCatalogue/LosApiDocs/LosApiDocs";

/* ===================== SERVICES ===================== */
import { apiGet, apiPost } from "../../apiService";

/* ===================== STYLES ===================== */
import styles from "./ApiCatalogue.module.scss";

const showDEV = true;

/**
 * ApiCatalogue
 * --------------------
 * User View  → Sidebar + Categories
 * Admin View → No Sidebar, Full screen API detail
 */
const ApiCatalogue = ({ isAdminView = false }) => {
  const navigate = useNavigate();
  const { id } = useParams();

  /* =========================
     STATE
  ========================= */
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("API"); // "API" | "LOS"
  const [introData, setIntroData] = useState("");
  const [apiName, setApiName] = useState("");
  const [selectedApi, setSelectedApi] = useState(null);
  const [requestBodyParams, setRequestBodyParams] = useState([]);
  const [responseBodyParams, setResponseBodyParams] = useState([]);
  const [errorBodyParams, setErrorBodyParams] = useState([]);
  const [, setApiKeysDetail] = useState([]);
  const [requestJson, setRequestJson] = useState(null);
  const [apiCode, setApiCode] = useState("");
  const [selectedErrorJson, setSelectedErrorJson] = useState(null);

  /* =========================
     RESPONSIVE SIDEBAR (USER ONLY)
  ========================= */
  useEffect(() => {
    if (isAdminView) return;

    const handleResize = () => {
      if (window.innerWidth > 1199) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isAdminView]);

  /* =========================
     ERROR DETAIL API (POST)
  ========================= */
  const handleErrorRowClick = async (errorId) => {
    try {
      const res = await apiPost("catalog", "/apimaster/error-code/response", {
        id: errorId,
      });
      const errorData = res?.data || null;
      setSelectedErrorJson(errorData);
    } catch (err) {
      console.error("Error detail API error", err);
    }
  };

  /* =========================
     AUTO LOAD FIRST ERROR
  ========================= */
  useEffect(() => {
    if (errorBodyParams.length > 0) {
      handleErrorRowClick(errorBodyParams[0].id);
    }
  }, [errorBodyParams]);

  const handleApiSelect = async (api) => {
    try {
      const res = await apiGet("catalog", `/apimaster/try/${api.id}`);
      const apiDetails = res.data?.apis?.[0];

      setRequestJson(apiDetails?.request_parameter || null);
      setIntroData(apiDetails?.introduction || "");
      setApiName(apiDetails?.name || "");
      setApiCode(apiDetails?.code || "");
      setSelectedApi({
        ...apiDetails,
        code_samples: res.data?.code_samples || [],
      });
      setRequestBodyParams(res.data?.request_details || []);
      setResponseBodyParams(res.data?.response_details || []);
      setErrorBodyParams(res.data?.error_details || []);
      setApiKeysDetail(res.data?.apiKeysDetail || []);
      setSidebarOpen(false);
    } catch (err) {
      console.error("Intro API error", err);
    }
  };

  /* =========================
     ADMIN AUTO LOAD BY URL
  ========================= */
  useEffect(() => {
    if (isAdminView && id) {
      handleApiSelect({ id });
    }
  }, [isAdminView, id]);

  /* =========================
     JSX
  ========================= */
  return (
    <div className={styles["admin-api-catalogue__page"]}>
      {/* HEADER SECTION */}
      <div className={styles["admin-api-catalogue__header"]}>
        {isAdminView && (
          <button
            className={styles["admin-api-catalogue__back-btn"]}
            onClick={() => navigate("/admin/admin-catalogue")}
            aria-label="Go Back"
          >
            <ArrowLeft size={20} strokeWidth={2.5} />
          </button>
        )}

        {!isAdminView && (
          <div className={styles["admin-api-catalogue__icon-wrapper"]}>
            <Server size={24} strokeWidth={2.5} />
          </div>
        )}

        <div className={styles["admin-api-catalogue__title-group"]}>
          <h2 className={styles["admin-api-catalogue__title"]}>
            API Catalogue
          </h2>
          <p className={styles["admin-api-catalogue__subtitle"]}>
            Comprehensive documentation for all Doql APIs
          </p>
        </div>
      </div>

      {/* TABS (user view only) */}
      {!isAdminView && (
        <div className={styles["admin-api-catalogue__tabs"]}>
          {["API", "LOS"].map((tab) => (
            <button
              key={tab}
              className={`${styles["admin-api-catalogue__tab"]} ${
                activeTab === tab
                  ? styles["admin-api-catalogue__tab--active"]
                  : ""
              }`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>
      )}

      {/* API TAB — existing catalogue (admin view always uses this) */}
      {(isAdminView || activeTab === "API") && (
        <div className={styles["admin-api-catalogue__layout"]}>
        {/* MOBILE HAMBURGER (USER ONLY) */}
        {!isAdminView && (
          <button
            className={styles["admin-api-catalogue__hamburger"]}
            onClick={() => setSidebarOpen((prev) => !prev)}
          >
            <Menu size={18} strokeWidth={2.5} />
            API Categories
          </button>
        )}

        {/* SIDEBAR OVERLAY (USER ONLY) */}
        {!isAdminView && (
          <div
            className={`${styles["admin-api-catalogue__sidebar-overlay"]} ${
              sidebarOpen
                ? styles["admin-api-catalogue__sidebar-overlay--open"]
                : ""
            }`}
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* LEFT PANEL (USER ONLY) */}
        {!isAdminView && (
          <div
            className={`${styles["admin-api-catalogue__left-panel"]} ${
              sidebarOpen ? styles["admin-api-catalogue__left-panel--open"] : ""
            }`}
          >
            <SidebarMenu onItemClick={handleApiSelect} />
          </div>
        )}

        {/* RIGHT PANEL (USER + ADMIN) */}
        <div className={styles["admin-api-catalogue__right-panel"]}>
          <div className={styles["admin-api-catalogue__content-header"]}>
            <h2 className={styles["admin-api-catalogue__api-name"]}>
              {apiName || "Select an API"}
            </h2>

            <Button
              text="Try Out"
              icon={Play}
              onClick={() => {
                if (isAdminView) {
                  navigate(`/admin/admin-catalogue/${id}/tryout`);
                } else {
                  navigate("/api-catalogue/api-tryout", {
                    state: { apiId: selectedApi?.id },
                  });
                }
              }}
            />
          </div>

          <ApiIntro intro={introData} />
          <ApiDetailsCard apiData={selectedApi} isAdminView={isAdminView} />
          <RequestBodyFormate request={requestJson} />
          <RequestHeaders code={apiCode} />
          <RequestBodyParams
            params={requestBodyParams}
            isAdminView={isAdminView}
            code={apiCode}
          />
          <ResponseBodyParams
            params={responseBodyParams}
            isAdminView={isAdminView}
          />
          <ErrorHandling
            errors={errorBodyParams}
            onRowClick={handleErrorRowClick}
            selectedJson={selectedErrorJson}
            isAdminView={isAdminView}
          />
          {showDEV && (
            <CodeSample codeSamples={selectedApi?.code_samples || []} />
          )}
          <SupportBox />
        </div>
      </div>
      )}

      {/* LOS TAB — OpenAPI documentation */}
      {!isAdminView && activeTab === "LOS" && <LosApiDocs />}
    </div>
  );
};

export default ApiCatalogue;
